/**
 * Profile screen container (specs/profile-page). Gathers data from the existing hooks/stores
 * and hands it to the presentational ProfileScreenView. No new data source — see plan.md.
 */

import React, { useCallback, useMemo } from 'react';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSupabaseAuth } from '../../../shared/contexts/SupabaseAuthContext';
import { useMenuStore } from '../../../shared/stores/menuStore';
import { useXpSummary } from '../../../economy/hooks/useXpSummary';
import { useUserProfile } from '../../../shared/contexts/UserProfileContext';
import { useAvatarContext } from '../../../avatars/contexts/AvatarContext';
import { useFriendsQuery } from '../../../friends/hooks/useFriendsQuery';
import { useDailyClaimAction } from '../../../economy/hooks/useDailyClaimAction';
import { getRank } from '../../../economy/helpers/rankRegistry.helper';
import { getRankColor } from '../../constants/rankColors.constants';
import {
  deriveXpDisplay,
  deriveStatTiles,
  deriveHeadToHeadRows,
} from '../../helpers/profileStats.helper';
import type { DisplayAvatarId } from '../../../avatars/types/avatar.types';
import { ProfileScreenView } from './ProfileScreenView';

function deriveDisplayName(user: ReturnType<typeof useSupabaseAuth>['user']): string {
  if (user) {
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    if (fullName) return fullName;
    if (user.username) return user.username;
    const email = user.primaryEmailAddress?.emailAddress;
    if (email) return email.split('@')[0];
  }
  return 'Player';
}

export const ProfileScreen: React.FC = () => {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { user, userId, isSignedIn } = useSupabaseAuth();
  const setMenuVisible = useMenuStore((s) => s.setMenuVisible);
  const openLayeredAvatarLab = useMenuStore((s) => s.openLayeredAvatarLab);

  const xpQuery = useXpSummary();
  const { profile, isLoading: profileLoading, error: profileError, refetchProfile } = useUserProfile();
  const { currentAvatarId, currentAvatarConfig } = useAvatarContext();
  const friendsQuery = useFriendsQuery(userId ?? '', !!userId && isSignedIn === true);
  const claimDaily = useDailyClaimAction();

  const xpSummary = xpQuery.data ?? null;
  const rank = xpSummary?.rank ?? 1;
  const rankDef = getRank(rank);

  const xp = useMemo(() => deriveXpDisplay(xpSummary), [xpSummary]);
  const statTiles = useMemo(() => deriveStatTiles(profile?.arenaStats), [profile?.arenaStats]);
  const h2hRows = useMemo(() => deriveHeadToHeadRows(friendsQuery.data), [friendsQuery.data]);

  const isLoading = (xpQuery.isLoading && !xpSummary) || (profileLoading && !profile);
  const hasError = xpQuery.isError || profileError != null;

  const handleBack = useCallback(() => {
    if (router.canGoBack()) router.back();
    else router.replace('/app/worldcup');
  }, [router]);

  const handleOpenSettings = useCallback(() => setMenuVisible(true), [setMenuVisible]);
  // No reopenMenuOnClose: closing the customizer must land back on Profile, not open the menu.
  const handleAvatarPress = useCallback(() => openLayeredAvatarLab(), [openLayeredAvatarLab]);
  const handleRetry = useCallback(() => {
    void xpQuery.refetch();
    void refetchProfile();
  }, [xpQuery, refetchProfile]);
  const handleRetryH2h = useCallback(() => {
    void friendsQuery.refetch();
  }, [friendsQuery]);

  return (
    <ProfileScreenView
      displayName={deriveDisplayName(user)}
      rankName={rankDef.name}
      tierColor={getRankColor(rank)}
      rankBadge={rankDef.badge}
      xp={xp}
      avatarId={(currentAvatarId ?? null) as DisplayAvatarId | null}
      avatarConfig={currentAvatarConfig}
      statTiles={statTiles}
      h2hRows={h2hRows}
      h2hError={friendsQuery.isError}
      onRetryH2h={handleRetryH2h}
      isLoading={isLoading}
      hasError={hasError}
      onRetry={handleRetry}
      onBack={handleBack}
      onOpenSettings={handleOpenSettings}
      onAvatarPress={handleAvatarPress}
      onCollectDaily={claimDaily}
      safeAreaTop={insets.top}
      safeAreaBottom={insets.bottom}
    />
  );
};
